import {useEffect} from 'react'
import {useMutation} from '@apollo/client'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {UPDATE_STATE_EU} from '../../../gql/mutations/statesMutations'
import {NodeStateEU} from '../../../types/statesEU'
import {parseId} from '../../../helpers'

interface IModalEditStateEUProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  info: NodeStateEU
}

interface IResUpdateState {
  updateState: {
    success: boolean
    errors?: {field: string | null; message: string}[]
  }
}

interface StateInput {
  name: string
  code: string
  jurisdictionType: string
  urlAcaQuote: string
}

const validationSchema = Yup.object({
  jurisdictionType: Yup.string().oneOf(['FEDERAL', 'STATE', ''], 'Invalid jurisdiction type'),
  urlAcaQuote: Yup.string().url('Must be a valid URL').nullable(),
})

const getFirstError = (errors?: {message?: string}[]) => errors?.[0]?.message || 'Something happened'

export const ModalEditStateEU: React.FC<IModalEditStateEUProps> = ({isOpen, onClose, refetch, info}) => {
  const [updateState, updateMeta] = useMutation<IResUpdateState>(UPDATE_STATE_EU)

  const formik = useFormik<StateInput>({
    initialValues: {
      name: info.name || '',
      code: info.code || '',
      jurisdictionType: info.jurisdictionType || '',
      urlAcaQuote: info.urlAcaQuote || '',
    },
    validationSchema,
    onSubmit: (values) => {
      updateState({
        variables: {
          id: parseId(info.id),
          stateData: {
            jurisdictionType: values.jurisdictionType || null,
            urlAcaQuote: values.urlAcaQuote.trim() || null,
          },
        },
      })
    },
  })

  useEffect(() => {
    formik.setValues({
      name: info.name || '',
      code: info.code || '',
      jurisdictionType: info.jurisdictionType || '',
      urlAcaQuote: info.urlAcaQuote || '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info])

  useEffect(() => {
    if (updateMeta.error) toast.error(`Error: ${updateMeta.error.message}`)
  }, [updateMeta.error])

  useEffect(() => {
    if (!updateMeta.data) return
    if (updateMeta.data.updateState?.success) {
      toast.success('State updated correctly')
      onClose()
      refetch()
    } else {
      toast.error(`Error: ${getFirstError(updateMeta.data.updateState?.errors)}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMeta.data])

  const isStateJurisdiction = formik.values.jurisdictionType === 'STATE'

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit State: {info.code}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='mb-3'>
            <Form.Label htmlFor='name'>Name</Form.Label>
            <Form.Control id='name' value={formik.values.name} readOnly plaintext className='form-control-plaintext fw-semibold' />
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='code'>Code</Form.Label>
            <Form.Control id='code' value={formik.values.code} readOnly plaintext className='form-control-plaintext fw-semibold' />
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='jurisdictionType'>Jurisdiction Type</Form.Label>
            <Form.Select
              id='jurisdictionType'
              value={formik.values.jurisdictionType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value=''>-- Not set --</option>
              <option value='FEDERAL'>Federal</option>
              <option value='STATE'>State (Own Exchange)</option>
            </Form.Select>
          </div>

          <div className='mb-3'>
            <Form.Label htmlFor='urlAcaQuote'>
              ACA Quote URL{' '}
              {!isStateJurisdiction && (
                <span className='text-muted fst-italic'>(only for State jurisdiction)</span>
              )}
            </Form.Label>
            <Form.Control
              id='urlAcaQuote'
              value={formik.values.urlAcaQuote}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.urlAcaQuote && formik.touched.urlAcaQuote)}
              placeholder='https://...'
              disabled={!isStateJurisdiction}
            />
            {formik.errors.urlAcaQuote && formik.touched.urlAcaQuote && (
              <Form.Text className='text-danger'>{formik.errors.urlAcaQuote}</Form.Text>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' disabled={updateMeta.loading}>
            {updateMeta.loading && <Spinner size='sm' animation='border' className='me-2' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
